import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import * as Sentry from '@sentry/react';
import './i18n'; // Initialize i18next
import { ColorModeContextProvider } from './contexts/ColorModeContext';
import { LanguageContextProvider } from './contexts/LanguageContext';
import { AnimationsProvider } from './contexts/AnimationsContext';
import { ZoomProvider } from './contexts/ZoomContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { SnackbarProvider } from './components/SnackbarProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { queryClient } from './lib/queryClient';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/login';
import { OrganizationList } from './pages/organizations/list';
import { OrganizationCreate } from './pages/organizations/create';
import { OrganizationEdit } from './pages/organizations/edit';
import { OrganizationShow } from './pages/organizations/show';
import { OrganizationTrash } from './pages/organizations/trash';
import { LocationList, LocationCreate, LocationEdit, LocationShow } from './pages/locations';
import { UserList } from './pages/users/list';
import { UserCreate } from './pages/users/create';
import { UserEdit } from './pages/users/edit';
import { SettingsPage } from './pages/settings';
import { SubscriptionDashboard } from './pages/subscriptions/dashboard';
import { PlanList } from './pages/subscriptions/plans/list';
import { PlanCreate } from './pages/subscriptions/plans/create';
import { PlanEdit } from './pages/subscriptions/plans/edit';
import { PlanShow } from './pages/subscriptions/plans/show';
import { ModuleList } from './pages/subscriptions/modules/list';
import { ModuleCreate } from './pages/subscriptions/modules/create';
import { ModuleEdit } from './pages/subscriptions/modules/edit';
import { RoleList, RoleCreate, RoleEdit } from './pages/roles';
import { LicenseList, LicenseGenerate, LicenseActivate } from './pages/licenses';

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
      <ErrorBoundary>
        <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ZoomProvider>
            <LanguageContextProvider>
              <ColorModeContextProvider>
              <AnimationsProvider>
              <NotificationsProvider>
                <SnackbarProvider>
                  <CssBaseline />
                  <GlobalStyles styles={{ html: { WebkitFontSmoothing: 'auto' } }} />
                  <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />

                  {/* Protected routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute requiredRoles={['super_admin']}>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/organizations" replace />} />

                    <Route path="organizations">
                      <Route index element={<OrganizationList />} />
                      <Route path="create" element={<OrganizationCreate />} />
                      <Route path="edit/:id" element={<OrganizationEdit />} />
                      <Route path="show/:id" element={<OrganizationShow />} />
                      <Route path="trash" element={<OrganizationTrash />} />
                    </Route>

                    <Route path="locations">
                      <Route index element={<LocationList />} />
                      <Route path="create" element={<LocationCreate />} />
                      <Route path="edit/:id" element={<LocationEdit />} />
                      <Route path="show/:id" element={<LocationShow />} />
                    </Route>

                    <Route path="users">
                      <Route index element={<UserList />} />
                      <Route path="create" element={<UserCreate />} />
                      <Route path="edit/:id" element={<UserEdit />} />
                    </Route>

                    <Route path="subscriptions">
                      <Route index element={<SubscriptionDashboard />} />
                      <Route path="plans">
                        <Route index element={<PlanList />} />
                        <Route path="create" element={<PlanCreate />} />
                        <Route path="edit/:id" element={<PlanEdit />} />
                        <Route path="show/:id" element={<PlanShow />} />
                      </Route>
                      <Route path="modules">
                        <Route index element={<ModuleList />} />
                        <Route path="create" element={<ModuleCreate />} />
                        <Route path="edit/:id" element={<ModuleEdit />} />
                      </Route>
                    </Route>

                    <Route path="roles">
                      <Route index element={<RoleList />} />
                      <Route path="create" element={<RoleCreate />} />
                      <Route path="edit/:id" element={<RoleEdit />} />
                    </Route>

                    <Route path="licenses">
                      <Route index element={<LicenseList />} />
                      <Route path="generate" element={<LicenseGenerate />} />
                      <Route path="activate" element={<LicenseActivate />} />
                    </Route>

                    <Route path="settings" element={<SettingsPage />} />

                    <Route path="*" element={<Navigate to="/organizations" replace />} />
                  </Route>
                  </Routes>
                </SnackbarProvider>
              </NotificationsProvider>
            </AnimationsProvider>
              </ColorModeContextProvider>
            </LanguageContextProvider>
          </ZoomProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}

export default App;
